/**
 * FSE Switch — MD3 MaterialSwitch.
 *
 * Design source: `Documentation/Claude design files/preview/switch.html`.
 * Spec: 52×32 track, thumb grows 16→24 on, track → primary (leaf) on /
 * surfaceContainerHighest off with an outline border, disabled at 40% opacity.
 * Wrapped in a ≥44dp tap target via a FormControlLabel row so the whole label
 * is pressable — important for the rural-low-vision audience.
 *
 * Wraps MUI `Switch`. Public prop surface is FSE-owned; MUI's `sx` is never
 * exposed.
 */
import {
  FormControlLabel,
  Switch as MuiSwitch,
  type SwitchProps as MuiSwitchProps,
} from '@mui/material';
import { forwardRef } from 'react';
import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, tap, typography } from '../theme/tokens.js';

export interface SwitchProps {
  /** On/off state. */
  checked: boolean;
  /** Fires with the next boolean state. */
  onChange: (checked: boolean) => void;
  /** Optional trailing label, rendered as a pressable row. */
  label?: string;
  /** Disable interaction (renders at 40% opacity per spec). */
  disabled?: boolean;
  /** Accessible label when no visible `label` is provided. */
  'aria-label'?: string;
}

const TRACK_W = 52;
const TRACK_H = 32;
const THUMB = 24; // constant thumb BOX; the off-state shrink is done with scale()
const MARGIN = (TRACK_H - THUMB) / 2; // 4dp — centres the thumb box in the track
const TRANSLATE = TRACK_W - THUMB - MARGIN * 2; // 20dp — slide distance when on
const OFF_SCALE = 16 / THUMB; // MD3 off-thumb is 16dp; scale (not resize) keeps it centred

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, label, disabled = false, 'aria-label': ariaLabel },
  ref,
) {
  // Mode-aware: built inside the body so the track/thumb colours read the active
  // mode's MD3 roles. In light these resolve to the same brand values the static
  // styles used (onPrimary=#FFF white thumb, primary=leaf[700] on-track,
  // outline=earth[500] off-thumb, surfaceContainerHighest≈paper[200] off-track,
  // outlineVariant=paper[200] track border, onSurfaceVariant=earth[700] label).
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];

  // Built on MUI's canonical pattern (no top/left overrides): the switchBase is
  // centred by `margin`, the checked state slides it with translateX, and the
  // MD3 off-thumb shrink is a `transform: scale` on the thumb — scale pivots on
  // the thumb's centre, so the box never moves and nothing clips in either state.
  const switchSx: NonNullable<MuiSwitchProps['sx']> = {
    width: TRACK_W,
    height: TRACK_H,
    padding: 0,
    display: 'inline-flex',
    flexShrink: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: `${MARGIN}px`,
      transitionDuration: '200ms',
      '&.Mui-checked': {
        transform: `translateX(${TRANSLATE}px)`,
        '& .MuiSwitch-thumb': {
          transform: 'scale(1)', // grow to full 24dp when on
          backgroundColor: sys.onPrimary, // white thumb (onPrimary)
        },
        '& + .MuiSwitch-track': {
          backgroundColor: sys.primary, // primary (leaf) when on
          border: 0,
          opacity: 1,
        },
      },
      '&.Mui-disabled': { opacity: 0.4 }, // README: "Disabled: 40% opacity"
    },
    '& .MuiSwitch-thumb': {
      width: THUMB,
      height: THUMB,
      boxShadow: 'none',
      backgroundColor: sys.outline, // MD3 outline-toned thumb when off
      transform: `scale(${OFF_SCALE})`,
      transformOrigin: 'center',
      transition: 'transform 200ms cubic-bezier(0.2,0,0,1), background-color 200ms',
    },
    '& .MuiSwitch-track': {
      borderRadius: `${radius.full}px`,
      backgroundColor: sys.surfaceContainerHighest, // surfaceContainerHighest-ish
      border: `2px solid ${sys.outlineVariant}`, // softer outline than the off-thumb
      opacity: 1,
      boxSizing: 'border-box',
    },
  };

  const control = (
    <MuiSwitch
      ref={ref}
      checked={checked}
      onChange={(_e, next) => onChange(next)}
      disabled={disabled}
      disableRipple
      slotProps={{ input: { 'aria-label': ariaLabel ?? label } }}
      sx={switchSx}
    />
  );

  if (!label) return control;

  return (
    <FormControlLabel
      control={control}
      label={label}
      disabled={disabled}
      sx={{
        marginLeft: 0,
        minHeight: tap.sm,
        gap: '12px',
        '& .MuiFormControlLabel-label': {
          fontFamily: typography.fontFamily,
          fontSize: typography.labelLarge.fontSize,
          fontWeight: 600,
          color: sys.onSurfaceVariant,
        },
      }}
    />
  );
});
